import {
  type IconConfig,
  type Project,
  suggestEntityIconSlug,
} from '@littlebird/sdk';
import { useId, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  type BaseModalFCProps,
  type ModalFC,
} from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { IconPickerTrigger } from '@/components/ui/icon-picker';
import { readLastIconColor } from '@/components/ui/icon-picker/icon-picker-preferences';
import { Input } from '@/components/ui/Input';
import { navigate } from '@/utils/navigation-service';

import { useProjectActions } from './project-actions';

export interface CreateProjectModalProps extends BaseModalFCProps {
  navigateOnCreate?: boolean;
  waitForServer?: boolean;
  onCreate?: (project: Project) => void | Promise<void>;
}

function defaultProjectIcon(): IconConfig | null {
  const color = readLastIconColor();
  return color ? { type: 'icon', code: null, color } : null;
}

export const CreateProjectModal: ModalFC<CreateProjectModalProps> = ({
  navigateOnCreate = true,
  waitForServer = false,
  open,
  onOpenChange,
  onCreate,
}) => {
  const { createProject } = useProjectActions();
  const [title, setTitle] = useState('');
  const [projectIcon, setProjectIcon] = useState<IconConfig | null>(
    defaultProjectIcon
  );
  const [iconSuggestionsEnabled, setIconSuggestionsEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameInputId = useId();

  const resetForm = () => {
    setTitle('');
    setProjectIcon(defaultProjectIcon());
    setIconSuggestionsEnabled(true);
  };

  const handleTitleChange = (nextTitle: string) => {
    setTitle(nextTitle);
    if (!iconSuggestionsEnabled) return;

    const suggestedIconSlug = suggestEntityIconSlug(nextTitle);
    setProjectIcon((currentIcon) => {
      const color = currentIcon?.color ?? null;
      return suggestedIconSlug
        ? { type: 'icon', code: suggestedIconSlug, color }
        : color
          ? { type: 'icon', code: null, color }
          : null;
    });
  };

  const handleIconChange = (nextIcon: IconConfig | null) => {
    setProjectIcon(nextIcon);
    setIconSuggestionsEnabled(false);
  };

  const dismissIconSuggestion = () => {
    setProjectIcon((currentIcon) => {
      const color = currentIcon?.color ?? null;
      return color ? { type: 'icon', code: null, color } : null;
    });
    setIconSuggestionsEnabled(false);
  };

  const handleSubmit = async () => {
    if (!title.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const project = await createProject(title, {
        waitForServer,
        projectIcon,
      });

      if (!project) {
        return;
      }

      await onCreate?.(project);
      resetForm();
      if (navigateOnCreate) {
        navigate({ to: `/projects/${project.id}` });
      }
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          resetForm();
        }

        onOpenChange(nextOpen);
      }}
    >
      <Dialog.Content
        showCloseButton
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <YStack className="gap-5">
          <Dialog.Title className="text-lg font-medium text-foreground">
            Create project
          </Dialog.Title>

          <YStack className="gap-2">
            <label
              htmlFor={nameInputId}
              className="text-foreground text-sm font-sans"
            >
              Project name
            </label>
            <XStack className="items-center gap-2">
              <IconPickerTrigger
                icon={projectIcon}
                label="Choose project icon"
                onChange={handleIconChange}
                onDismissSuggestion={
                  iconSuggestionsEnabled && projectIcon?.code
                    ? dismissIconSuggestion
                    : undefined
                }
              />
              <Input
                id={nameInputId}
                ref={inputRef}
                type="text"
                value={title}
                onChange={(event) => handleTitleChange(event.target.value)}
                placeholder="Copenhagen Trip"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            </XStack>
            <XStack className="gap-3 rounded-xl border border-dashed border-border bg-surface-active px-3.5 py-3">
              <Icons.lightbulb className="size-4 text-foreground-muted shrink-0 mt-0.5" />
              <span className="text-xs text-foreground-muted leading-relaxed">
                Projects keep chats and meeting notes together in one place. Use
                them for ongoing work, or just to keep things tidy.
              </span>
            </XStack>
          </YStack>

          <XStack className="justify-end">
            <Button
              variant="default"
              onClick={() => void handleSubmit()}
              disabled={!title.trim() || isSubmitting}
            >
              Create project
            </Button>
          </XStack>
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};
